import React, { ReactNode, useCallback } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ActionLink } from 'components/action-button';
import Modal, { ModalContent } from 'components/modal';
import { OrderIssues } from 'pages/order-confirmation/common';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import HeadingContainer from './cart-error-heading-wrapper';

const ButtonGutter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;

  ${Styles.desktop`
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const Body = styled.p`
  color: ${Styles.color.black};
  font-size: 0.9375rem;
  font-weight: 400;
  text-align: center;
  margin: 0.5rem 0 2rem;
`;

const Disclaimer = styled.p`
  font-size: 0.75rem;
  font-family: ${Styles.fontFamily.body};
  max-width: 12.875rem;
  text-align: center;
  margin-top: 2rem;
`;

interface ICartError {
  title?: string;
  body?: ReactNode;
  disclaimer?: string;
  startOver?: boolean;
  onAccept?: () => void;
}

const StyledActionLink = styled(ActionLink)`
  ${Styles.mobile`
    font-size: 1.1rem;
  `}
`;

const StyledModalContent = styled(ModalContent)`
  .modal-inner {
    width: 100%;
  }
`;

const CartError: React.FC<ICartError> = ({ title, body, disclaimer, onAccept, startOver }) => {
  const isShowingOrderingIssue = useFlag(LaunchDarklyFlag.SHOW_ORDERING_ISSUE);
  const { emptyCart, selectServiceMode } = useOrderContext();
  const { resetStore } = useStoreContext();
  const { shouldConfirmStoreOnCheckout } = useUIContext();
  const { onCancelRedemption } = useOffersContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const startOverCallback = useCallback(() => {
    selectServiceMode(null);
    onCancelRedemption();
    shouldConfirmStoreOnCheckout(true);
    emptyCart();
    resetStore();
  }, [emptyCart, onCancelRedemption, resetStore, selectServiceMode, shouldConfirmStoreOnCheckout]);

  const modalHeading = title || formatMessage({ id: 'restaurantUnavailableErrorTitle' });
  const modalBody = body || formatMessage({ id: 'orderNotCompleted' });

  return (
    <Modal
      onDismiss={() => navigate(routes.menu)}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Cart Error: Order could not be completed',
        modalMessage: typeof modalBody === 'string' ? modalBody : undefined,
      }}
    >
      <StyledModalContent>
        <HeadingContainer modalHeading={modalHeading} />
        <Body style={{ whiteSpace: 'pre-line', textAlign: 'justify' }}>{modalBody}</Body>

        <ButtonGutter>
          {onAccept && (
            <Box margin="0 auto">
              <ActionLink onClick={onAccept} to={'.'} fullWidth>
                {formatMessage({ id: 'accept' })}
              </ActionLink>
            </Box>
          )}
          {startOver && (
            <Box margin="0 auto">
              <StyledActionLink
                to={routes.menu}
                onClick={startOverCallback}
                data-testid="cart-error-start-over"
                fullWidth
              >
                {formatMessage({ id: 'restartPurchaseProcessButton' })}
              </StyledActionLink>
            </Box>
          )}
        </ButtonGutter>

        <Disclaimer>
          {disclaimer || formatMessage({ id: 'restartPurchaseProcessDisclaimer' })}
        </Disclaimer>

        {isShowingOrderingIssue && <OrderIssues />}
      </StyledModalContent>
    </Modal>
  );
};

export default CartError;
